class FacetFiltersForm extends HTMLElement {
  constructor() {
    super()
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 800)

    const facetForm = this.querySelector('form')
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this))
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial
      if (searchParams === FacetFiltersForm.searchParamsPrev) return
      FacetFiltersForm.renderPage(searchParams, null, false)
    }
    window.addEventListener('popstate', onHistoryChange)
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams
    const sections = FacetFiltersForm.getSections()
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`
      const filterDataUrl = (element) => element.url === url

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event)
    })

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams)
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }]
        FacetFiltersForm.renderFilters(html, event)
        FacetFiltersForm.renderProductGridContainer(html)
        FacetFiltersForm.renderProductCount(html)
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML)
      })
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html
    FacetFiltersForm.renderFilters(html, event)
    FacetFiltersForm.renderProductGridContainer(html)
    FacetFiltersForm.renderProductCount(html)
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML)
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML
    window?.ajaxinateResults()
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount')
    container.innerHTML = count
    container.classList.remove('loading')
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    )
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'))
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetSortForm .js-filter'
    )
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetSortForm .js-filter'
    )

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove()
      }
    })

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined
      return jsFilter ? element.id === jsFilter.id : false
    }

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element))
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId)

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id)
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1]
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender)
            return
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender)
        }
      }
    })

    FacetFiltersForm.renderActiveFacets(parsedHTML)
    FacetFiltersForm.renderAdditionalElements(parsedHTML)
    FacetFiltersForm.renderGroupClearButtons(parsedHTML)
    FacetFiltersForm.renderFiltersOptionsWrapper(parsedHTML)

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'))
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID))

        const newFacetDetailsElement = document.getElementById(closestJSFilterID)
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector)

        const isTextInput = event.target.getAttribute('type') === 'text'

        if (newElementToActivate && !isTextInput) newElementToActivate.focus()
      }
    }
  }

  static renderGroupClearButtons(html) {
    const facetGroupSelectors = ['.collection__filters-options-group.js-filter']
    facetGroupSelectors.forEach((selector) => {
      const facetGroups = html.querySelectorAll(selector)
      if (facetGroups.length > 0) {
        facetGroups.forEach((group) => {
          const groupItems = group.querySelectorAll('.collection__filters-options-list-item input[type="checkbox"]')
          let checkedCount = 0
          if (groupItems.length > 0) {
            groupItems.forEach((item) => {
              if (item.checked === true) {
                checkedCount++
              }
            })

            const documentGroup = document.getElementById(group.id)
            if (documentGroup) {
              const documentClearButton = documentGroup.querySelector('.collection__filters-options-clear')
              if (checkedCount > 0) {
                documentClearButton.classList.remove('disabled')
              } else {
                documentClearButton.classList.add('disabled')
              }
            }
          }
        })
      }
    })
  }

  static renderFiltersOptionsWrapper(html) {
    const targetFiltersOptionsWrapper = document.querySelector('.collection__filters-options-wrapper')
    const sourceFiltersOptionsWrapper = html.querySelector('.collection__filters-options-wrapper')

    if (sourceFiltersOptionsWrapper && targetFiltersOptionsWrapper) {
      targetFiltersOptionsWrapper.className = sourceFiltersOptionsWrapper.className
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets']

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector)
      if (!activeFacetsElement) return
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML
    })

    FacetFiltersForm.toggleActiveFacets(false)
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.sorting']

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML
    })
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary')
    const sourceSummary = source.querySelector('.facets__summary')

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML
    }

    const targetHeaderElement = target.querySelector('.facets__header')
    const sourceHeaderElement = source.querySelector('.facets__header')

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML
    }

    const targetWrapElement = target.querySelector('.facets-wrap')
    const sourceWrapElement = source.querySelector('.facets-wrap')

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'))
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'))
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list')
    const sourceFacetsList = source.querySelector('.mobile-facets__list')

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`)
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form)
    return new URLSearchParams(formData).toString()
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event)
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const sortFilterForms = document.querySelectorAll('facet-filters-form form')
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = []
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile'

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form))
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form))
        }
      })
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    FacetFiltersForm.toggleActiveFacets()
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1)
    FacetFiltersForm.renderPage(url)
  }
}

FacetFiltersForm.filterData = []
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1)
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1)
customElements.define('facet-filters-form', FacetFiltersForm)
FacetFiltersForm.setListeners()

class FacetRemove extends HTMLElement {
  constructor() {
    super()
    const facetLink = this.querySelector('a')
    facetLink.setAttribute('role', 'button')
    facetLink.addEventListener('click', this.closeFilter.bind(this))
    this.type = ''
    if (facetLink.dataset.type) {
      this.type = facetLink.dataset.type
    } else {
      this.type = 'single'
    }
  }

  closeFilter(event) {
    event.preventDefault()
    if (this.type === 'single') {
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form')
      form.onActiveFilterClick(event)
    } else if (this.type === 'group') {
      const facetLink = this.querySelector('a')
      const targetGroupId = facetLink.dataset.groupId
      const targetGroupList = this.parentElement.parentElement.querySelector(`#${ targetGroupId }`)
      const targets = targetGroupList.querySelectorAll('input[type="checkbox"]:checked')
      if (targets.length > 0) {
        targets.forEach((target) => {
          target.parentElement.click()
        })
      }
    }
  }
}

customElements.define('facet-remove', FacetRemove)

// Custom non-facet behaviors
function debounce(fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

// Filters
document.addEventListener('click', function(event) {
  const collapsibleTriggers = document.querySelectorAll('.collection__filters-options-title h3 span')
  if (collapsibleTriggers.length > 0) {
    collapsibleTriggers.forEach((trigger) => {
      const triggerTargetId = trigger.dataset.collapsibleTargetId
      const triggerTarget = document.getElementById(triggerTargetId)
      if (triggerTarget && trigger.contains(event.target)) {
        triggerTarget.classList.toggle('hidden')
      }
    })
  }
})

const toggleFiltersOnMobile = () => {
  const stickyToggle = document.querySelector('.collection__filter-sticky-toggle')
  const collectionFilters = document.querySelector('.hide-filters')
  const collectionContent = document.querySelector('.collection__contents')
  const collectionFiltersHide = document.querySelector('.collection__filters-hide')
  const collectionFiltersShow = document.querySelector('.collection__filters-show')

  if (window.innerWidth < 1024) {
    stickyToggle.classList.remove('show')
    collectionFilters.classList.remove('hide-filters')
    collectionContent.classList.remove('hide-filters')
    collectionFiltersHide.classList.remove('visually-hidden')
    collectionFiltersShow.classList.add('visually-hidden')
  }
}
document.addEventListener('DOMContentLoaded', toggleFiltersOnMobile);
window.addEventListener('resize', toggleFiltersOnMobile);

// Sorting
document.addEventListener('click', function(event) {
  const dropdown = document.querySelector('.collection__sorting-choices-dropdown')
  const list = document.querySelector('.collection__sorting-choices-list')
  
  if (dropdown && list && list.contains(event.target)) {
    dropdown.classList.toggle('hidden')
    return
  }

  if (dropdown && !dropdown.contains(event.target) && (!list || !list.contains(event.target))) {
    dropdown.classList.add('hidden')
  }
})
